import { Navigate, useLocation, type NavigateProps } from "react-router-dom";
import {
  getDefaultRoute,
  getImpersonationRoute,
} from "@/core/components/default-route";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { getProductCapabilities } from "@/business/product-capabilities";
import { useGetBusiness } from "@/business/query";
import { CHANGELOG_PATH } from "@/changelog/routes";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import { isHelpPath } from "@/help/routes";
import {
  canManageSimpleOnboarding,
  hasOnboardingProducts,
  isSimpleOnboardingRequired,
  requiresSimpleOnboardingRedirect,
  resumeOnboardingPath,
  SIMPLE_ONBOARDING_PATHS,
} from "@/onboarding/routes";
import { NoOnboardingProducts } from "@/onboarding/simple-onboarding/no-onboarding-products";
import { OnboardingLoadError } from "@/onboarding/simple-onboarding/onboarding-load-error";
import { useBusinessOnboarding } from "@/onboarding/simple-onboarding/shared-query";
import {
  isPayBreakdownPath,
  PAY_BREAKDOWN_PATH,
} from "@/payroll/pay-breakdown/routes";
import { useCanViewPayrollRoster } from "@/payroll/roster/permissions";
import { payrollSettingsAccessPolicy } from "@/payroll/settings/access-policy";
import {
  isPayrollSettingsPath,
  isPayrollEditorPath,
} from "@/payroll/settings/routes";
import { useWorkbookAccessEditorAccess } from "@/payroll/workbook-access/permissions";
import { getMarketingProduct } from "@/product-marketing/routes";
import { isProfitabilityPath } from "@/profitability/routes";
import { useGetMyUser } from "@/user/query";
import { isTech, isApOnly } from "@/user/utils";

const ROUTES = {
  login: "/login",
  onboarding: SIMPLE_ONBOARDING_PATHS.root,
  onboardingDefault: SIMPLE_ONBOARDING_PATHS.welcome,
  waitingForSetup: "/waiting-for-setup",
  profile: "/profile",
  referrals: "/referrals",
  accountsPayable: "/accounts-payable",
  businessSettings: "/business-settings",
  payBreakdown: PAY_BREAKDOWN_PATH,
} as const;

const TECH_ALLOWED_PATH_PREFIXES: string[] = [ROUTES.profile];

const PageSpinner = () => (
  <Stack gap={4} className="h-full w-full" justify="center" align="center">
    <Spinner layout="centered" aria-label="Loading page" />
  </Stack>
);

const Redirect = (props: NavigateProps) => (
  <>
    <Navigate {...props} />
    <PageSpinner />
  </>
);

export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();

  const { isAuthenticated, isLoading, token, logout } = useAuth();
  const { businessId } = useBusiness();
  const { settings, isLoading: isProductsLoading } = useBusinessProducts();

  const { data: user, isLoading: isUserLoading } = useGetMyUser();
  const { data: business, isLoading: isBusinessLoading } =
    useGetBusiness(businessId);
  const { enabled: documentsOnly, isLoading: isFlagLoading } = useFeatureFlag(
    FEATURE_FLAG_NAMES.profitability_documents_only,
  );
  const isImpersonationLanding =
    location.pathname === "/" && location.state?.fromImpersonation === true;
  const capabilities = getProductCapabilities({
    user,
    business,
    settings,
    documentsOnly,
  });
  const enableAp = capabilities.enabled.accounts_payable;
  const enableReferrals = capabilities.enabled.referrals;
  const apOnly = isApOnly(user?.role);
  const technician = isTech(user?.role);
  const canReachPayrollEditor =
    isPayrollEditorPath(location.pathname) &&
    (!apOnly || capabilities.payroll.canEditRules);
  const needsOnboarding = isSimpleOnboardingRequired(business?.onboarding_step);
  const canManageOnboarding = canManageSimpleOnboarding(user?.role);
  const onboardingQuery = useBusinessOnboarding(
    businessId,
    isAuthenticated && needsOnboarding && canManageOnboarding && !apOnly,
  );

  const { canViewRoster: canUseRoster, isLoading: isRosterAccessLoading } =
    useCanViewPayrollRoster(business, user);
  const { canManage: canManageAccess, isAccessLoading } =
    useWorkbookAccessEditorAccess(businessId);
  const payrollSettings = payrollSettingsAccessPolicy({
    canViewRoster: canUseRoster,
    canManageAccess,
  });
  const isPayrollSettingsGate =
    isPayrollSettingsPath(location.pathname) ||
    location.pathname.startsWith(ROUTES.businessSettings);

  if (isAuthenticated && token && !isUserLoading && !user) {
    console.warn(
      "Detected corrupted auth state: token exists but no user data. Logging out...",
    );
    logout();
    return null;
  }

  if (
    isLoading ||
    isUserLoading ||
    isBusinessLoading ||
    isProductsLoading ||
    isFlagLoading ||
    onboardingQuery.isLoading ||
    ((isRosterAccessLoading || isAccessLoading) && isPayrollSettingsGate)
  ) {
    return <PageSpinner />;
  }

  if (!isAuthenticated || !token) {
    return <Redirect to={ROUTES.login} state={{ from: location }} replace />;
  }

  if (location.pathname === CHANGELOG_PATH) return <>{children}</>;

  if (canReachPayrollEditor) return <>{children}</>;

  if (apOnly) {
    const isApRoute =
      location.pathname === ROUTES.accountsPayable ||
      location.pathname.startsWith(`${ROUTES.accountsPayable}/`);
    if (
      location.pathname === ROUTES.profile ||
      (enableAp && (isApRoute || isHelpPath(location.pathname)))
    ) {
      return <>{children}</>;
    }
    return (
      <Redirect
        to={enableAp ? ROUTES.accountsPayable : ROUTES.profile}
        replace
      />
    );
  }
  const isCurrentPayBreakdownRoute = isPayBreakdownPath(location.pathname);

  if (needsOnboarding) {
    if (!canManageOnboarding) {
      return (
        <Redirect
          to={ROUTES.waitingForSetup}
          state={{ from: location }}
          replace
        />
      );
    }

    if (!hasOnboardingProducts(business?.enabled_products))
      return <NoOnboardingProducts />;

    if (onboardingQuery.isError && !onboardingQuery.data) {
      return (
        <OnboardingLoadError retry={() => void onboardingQuery.refetch()} />
      );
    }

    if (
      requiresSimpleOnboardingRedirect(
        business?.onboarding_step,
        location.pathname,
      )
    ) {
      return (
        <Redirect
          to={
            onboardingQuery.data
              ? resumeOnboardingPath(onboardingQuery.data)
              : ROUTES.onboardingDefault
          }
          state={{ from: location }}
          replace
        />
      );
    }

    return <>{children}</>;
  }

  if (isHelpPath(location.pathname) && !technician) return <>{children}</>;
  const marketingProduct = getMarketingProduct(location.pathname);
  if (marketingProduct && capabilities.preview[marketingProduct]) {
    return <>{children}</>;
  }

  if (payrollSettings.canReach(location.pathname)) return <>{children}</>;

  if (location.pathname.startsWith(`/business-settings/${businessId}/users`))
    return <>{children}</>;

  if (isProfitabilityPath(location.pathname)) {
    if (!technician || capabilities.profitability.canAccess)
      return <>{children}</>;
  }

  if (technician) {
    const techDefaultRoute = ROUTES.payBreakdown;
    const isAllowedTechRoute =
      isCurrentPayBreakdownRoute ||
      TECH_ALLOWED_PATH_PREFIXES.some((route) =>
        location.pathname.startsWith(route),
      );

    if (location.pathname === "/") {
      return (
        <Redirect to={techDefaultRoute} state={{ from: location }} replace />
      );
    }

    if (!isAllowedTechRoute) {
      return (
        <Redirect to={techDefaultRoute} state={{ from: location }} replace />
      );
    }

    return <>{children}</>;
  }

  const hasPayBreakdownAccess = capabilities.payroll.hasOutput;
  const isApRoute = location.pathname.startsWith("/accounts-payable");
  const defaultRoute = getDefaultRoute({
    hasPayBreakdownAccess,
    enableReferrals,
    enableAp,
    canOpenProfitability: capabilities.profitability.canOpenReports,
  });

  if (
    !enableReferrals &&
    location.pathname.startsWith(ROUTES.businessSettings)
  ) {
    return <Redirect to={payrollSettings.landing ?? defaultRoute} replace />;
  }

  if (location.pathname === "/") {
    const landingRoute = isImpersonationLanding
      ? getImpersonationRoute({
          hasPayBreakdownAccess,
          enableReferrals,
          enableAp,
          enablePayroll: capabilities.enabled.payroll,
          canOpenProfitability: capabilities.profitability.canOpenReports,
        })
      : defaultRoute;
    return <Redirect to={landingRoute} state={{ from: location }} replace />;
  }

  if (
    !enableReferrals &&
    location.pathname !== ROUTES.profile &&
    !location.pathname.startsWith(ROUTES.onboarding) &&
    !(enableAp && isApRoute) &&
    !(isCurrentPayBreakdownRoute && hasPayBreakdownAccess)
  ) {
    return <Redirect to={defaultRoute} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
